import React from "react";
import { Link } from "react-router-dom";
import { Cake, Tent, School, Building2, Sparkles, Check, Clock, Users } from "lucide-react";
import { useSeo } from "@/lib/seo";
import { Reveal } from "@/components/Reveal";
import { WaveTop } from "@/components/Deco";
import BookCta from "@/components/BookCta";

const services = [
  {
    icon: Cake, color: "#F43BA5", tint: "bg-hotpink/15",
    title: "Birthday Parties",
    text: "Fun face painting for birthdays of all ages. Choose your theme and favourite characters or designs when you send your booking request.",
    points: ["Kids, teens & grown-ups", "Themed & character designs", "Setup and cleanup included"],
  },
  {
    icon: Tent, color: "#28BCEB", tint: "bg-brightblue/15",
    title: "Markets, Festivals & Fairs",
    text: "Face painting for public events, markets, festivals, community celebrations, and high-volume days with a steady stream of happy faces.",
    points: ["Great for busy crowds", "Quick cheek art to full designs", "Professional & reliable"],
  },
  {
    icon: School, color: "#65A95A", tint: "bg-leaf/15",
    title: "Schools & Community Events",
    text: "Professional face painting for school celebrations, fundraisers, family nights, and community programs. Licensed and insured for peace of mind.",
    points: ["Licensed & insured", "Family-friendly designs", "Fundraisers & family nights"],
  },
  {
    icon: Building2, color: "#8754D9", tint: "bg-purple/15",
    title: "Corporate & Special Events",
    text: "Face painting for company parties, grand openings, staff celebrations, weddings, holiday events, and private functions.",
    points: ["Elegant options for adults", "Weddings & holiday events", "Professional presentation"],
  },
];

export default function Services() {
  useSeo({ title: "Services — Face Painting for Events in Nanaimo", description: "Face painting services for birthday parties, markets, festivals, schools, corporate events and weddings in Nanaimo and surrounding communities." });

  return (
    <div data-testid="services-page">
      <section className="section pt-14 pb-10 text-center">
        <Reveal>
          <p className="font-accent text-3xl text-hotpink">Colourful fun for every occasion</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-700 mb-4">What Tay can bring to your event</h1>
          <p className="max-w-2xl mx-auto text-lg text-ink/80">From tiny tigers to sparkling butterflies — and plenty of wild ideas in between. Prices are tailored to your event, so pick the closest fit and Tay will confirm the details.</p>
        </Reveal>
      </section>

      <section className="section pb-6 grid md:grid-cols-2 gap-6">
        {services.map((s, i) => (
          <Reveal key={s.title} delay={i * 0.08}>
            <div className="card-soft p-7 h-full">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-2xl border-[3px] border-ink flex items-center justify-center" style={{ backgroundColor: s.color }}>
                  <s.icon className="w-7 h-7 text-white" />
                </div>
                <h2 className="font-heading text-2xl font-600">{s.title}</h2>
              </div>
              <p className="text-ink/80 font-body mb-4">{s.text}</p>
              <ul className="space-y-2 mb-5">
                {s.points.map((p) => (
                  <li key={p} className="flex items-center gap-2 font-body"><span className={`w-6 h-6 rounded-full border-2 border-ink flex items-center justify-center ${s.tint}`}><Check className="w-4 h-4" /></span>{p}</li>
                ))}
              </ul>
              <Link to="/book" className="btn-pink !py-2.5" data-testid={`service-book-${i}`}>Book this</Link>
            </div>
          </Reveal>
        ))}
      </section>

      <section className="section pb-14">
        <Reveal>
          <div className="card-pop p-8 bg-sunshine/25 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-purple border-[3px] border-ink flex items-center justify-center mb-3"><Sparkles className="w-7 h-7 text-white" /></div>
            <h2 className="font-heading text-3xl font-700 mb-2">Custom Events</h2>
            <p className="max-w-xl mx-auto text-ink/80 mb-3">Have something that doesn’t fit a standard package? Big festival, unusual venue, multi-day event? Tell Tay the details and she’ll put together a custom quote just for you.</p>
            <div className="flex flex-wrap gap-3 justify-center text-ink/70 mb-5">
              <span className="chip"><Clock className="w-4 h-4" /> Flexible hours</span>
              <span className="chip"><Users className="w-4 h-4" /> Any size crowd</span>
            </div>
            <Link to="/book?type=custom" className="btn-blue" data-testid="custom-quote-btn">Request a custom quote</Link>
          </div>
        </Reveal>
      </section>

      <BookCta />
    </div>
  );
}
